import { __decorate, __metadata } from 'tslib';
import * as i0 from '@angular/core';
import { Component, ViewEncapsulation, ChangeDetectionStrategy, EventEmitter, booleanAttribute, Input, Output } from '@angular/core';
import { CSSComponent, CSSModifier } from '@odx/angular/internal';
import { injectElement } from '@odx/angular/utils';
import { CoreModule } from '@odx/angular';
import { ButtonComponent } from '@odx/angular/components/button';
import { IconComponent } from '@odx/angular/components/icon';

/**
 * Represents a row in the chip list.
 */
let ChipListRowComponent = class ChipListRowComponent {
    constructor() {
        this.element = injectElement();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ChipListRowComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: ChipListRowComponent, isStandalone: true, selector: "odx-chip-list-row", ngImport: i0, template: "<ng-content select=\"odx-chip\" />\n", changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
};
ChipListRowComponent = __decorate([
    CSSComponent('chip-list-row')
], ChipListRowComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ChipListRowComponent, decorators: [{
            type: Component,
            args: [{ selector: 'odx-chip-list-row', encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, standalone: true, template: "<ng-content select=\"odx-chip\" />\n" }]
        }] });

/**
 * Represents a component that displays a list of chips.
 */
let ChipListComponent = class ChipListComponent {
    constructor() {
        this.element = injectElement();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ChipListComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: ChipListComponent, isStandalone: true, selector: "odx-chip-list", ngImport: i0, template: "<ng-content select=\"odx-chip-list-row\" />\n<ng-content select=\"odx-autocomplete\" />\n", changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
};
ChipListComponent = __decorate([
    CSSComponent('chip-list')
], ChipListComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ChipListComponent, decorators: [{
            type: Component,
            args: [{ selector: 'odx-chip-list', encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, standalone: true, template: "<ng-content select=\"odx-chip-list-row\" />\n<ng-content select=\"odx-autocomplete\" />\n" }]
        }] });

const ChipSize = {
    SMALL: 'small',
    MEDIUM: 'medium',
};

const ChipVariant = {
    SECONDARY: 'secondary',
    HIGHLIGHT: 'highlight',
    SUCCESS: 'success',
    DANGER: 'danger',
    WARNING: 'warning',
    CONFIRMATION: 'confirmation',
};

/**
 * Represents a chip component.
 */
let ChipComponent = class ChipComponent {
    constructor() {
        this.element = injectElement();
        /**
         * Indicates whether the chip is removable.
         *
         * @type {boolean}
         * @default false
         */
        this.removable = false;
        /**
         * The size of the chip.
         *
         * @type {ChipSize}
         * @default ChipSize.SMALL
         */
        this.size = ChipSize.SMALL;
        /**
         * The variant of the chip.
         *
         * @type {ChipVariant}
         * @default ChipVariant.SECONDARY
         */
        this.variant = ChipVariant.SECONDARY;
        /**
         * Event emitter that emits when the chip is removed.
         *
         * @emits {void}
         */
        this.remove = new EventEmitter();
    }
    onClick() {
        this.remove.emit();
    }
    get buttonVariant() {
        return this.variant === ChipVariant.WARNING ? null : this.variant;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ChipComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.13", type: ChipComponent, isStandalone: true, selector: "odx-chip", inputs: { removable: ["removable", "removable", booleanAttribute], size: "size", variant: "variant" }, outputs: { remove: "remove" }, ngImport: i0, template: "<ng-content select=\"odx-icon\" />\n<div class=\"odx-chip__content\">\n  <ng-content />\n</div>\n@if (removable) {\n  <button odxButton class=\"odx-chip__action\" [size]=\"size\" [variant]=\"buttonVariant\" type=\"button\" (click)=\"onClick()\">\n    <odx-icon [size]=\"size\" name=\"close\" iconSet=\"core\" />\n  </button>\n}\n", dependencies: [{ kind: "ngmodule", type: CoreModule }, { kind: "component", type: ButtonComponent, selector: "button[odxButton], a[odxButton]", inputs: ["variant", "size"] }, { kind: "component", type: IconComponent, selector: "odx-icon", inputs: ["inline", "size", "name", "iconSet", "identifier"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
};
__decorate([
    CSSModifier(),
    __metadata("design:type", String)
], ChipComponent.prototype, "size", void 0);
__decorate([
    CSSModifier(),
    __metadata("design:type", String)
], ChipComponent.prototype, "variant", void 0);
ChipComponent = __decorate([
    CSSComponent('chip')
], ChipComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ChipComponent, decorators: [{
            type: Component,
            args: [{ selector: 'odx-chip', imports: [CoreModule, ButtonComponent, IconComponent], encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, standalone: true, template: "<ng-content select=\"odx-icon\" />\n<div class=\"odx-chip__content\">\n  <ng-content />\n</div>\n@if (removable) {\n  <button odxButton class=\"odx-chip__action\" [size]=\"size\" [variant]=\"buttonVariant\" type=\"button\" (click)=\"onClick()\">\n    <odx-icon [size]=\"size\" name=\"close\" iconSet=\"core\" />\n  </button>\n}\n" }]
        }], propDecorators: { removable: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }], size: [{
                type: Input
            }], variant: [{
                type: Input
            }], remove: [{
                type: Output
            }] } });

/**
 * Generated bundle index. Do not edit.
 */

export { ChipComponent, ChipListComponent, ChipListRowComponent, ChipSize, ChipVariant };
